import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import AppButton from '@/components/atoms/AppButton';
import AppSpin from '@/components/atoms/AppSpin';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomVisitClinicBar from '@/components/organisms/VisitClinicCalendar/BottomVisitClinicBar';
import VisitClinicSearchBar from '@/components/organisms/VisitClinicCalendar/VisitClinicSearchBar';
import VisitClinicMassagerSelect from '@/components/organisms/VisitClinicCalendar/VisitClinicMassagerSelect';
import VisitClinicCalendarTable from '@/components/organisms/VisitClinicCalendar/VisitClinicCalendarTable';
import VisitClinicPriceSection from '@/components/organisms/VisitClinicCalendar/VisitClinicPriceSection';
import VisitClinicConsentSection from '@/components/organisms/VisitClinicCalendar/VisitClinicConsentSection';
import VisitClinicInsuranceSection from '@/components/organisms/VisitClinicCalendar/VisitClinicInsuranceSection';
import { revealConsentDifferenceReason } from '@/components/organisms/VisitClinicCalendar/consentDifferenceReason';
import {
  reasonRequiredRezeptIds,
  storeChangesR2Parts,
} from '@/components/organisms/VisitClinicCalendar/reasonRequired';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  useVisitClinicScheduleQuery,
  useVisitClinicStoreMutation,
  useVisitClinicBulkDeleteMutation,
  resetVisitClinicScheduleForPatient,
} from '@/hooks/main/useVisitClinic';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import useAppModal from '@/hooks/useAppModal';
import { NotFound } from '@/pages/main/system/NotFound';
import {
  clearLatestEditingPatientId,
  setLatestEditingPatientId,
} from '@/shared/lib/latestEditingPatient';
import { useAuth } from '@/shared/providers/AuthContext';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import type {
  PendingVisitData,
  VisitClinicRezeptField,
  VisitClinicStorePayload,
} from '@/types/visitClinic.types';
import { parseYearmonthParam } from '@/utils/dateConverter';

const LOCKED_CHANGE_MESSAGE = 'ロックされているため、変更できません。';

/**
 * VisitClinicEditPage — 施術入力カレンダー (visit_clinic/edit/{patientId}/{yearmonth}).
 * Supports both A2 (鍼灸) and R2 (マッサージ) service types.
 * Layout: 4 collapsible panels + fixed bottom action bar (matches legacy A2).
 */
const VisitClinicEditPage: React.FC = () => {
  const {
    patientId: patientIdStr,
    yearmonth: rawYearmonth,
    tenant,
  } = useParams<{
    patientId: string;
    yearmonth: string;
    tenant: string;
  }>();
  // URL param may be Japanese era "令和08年04月" (A2 format) or YYYY-MM (legacy); normalize to YYYY-MM
  const yearmonth = rawYearmonth ? parseYearmonthParam(rawYearmonth) : undefined;
  const patientId = patientIdStr ? Number(patientIdStr) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clinic } = useAuth();
  const theme = useTheme();
  const serviceType = theme?.serviceType ?? 'A2';

  /**
   * A2 schedule.blade.js `culum_control('select_massager_id')`:
   * - `#schedule` is hidden when `select_massager_id` is empty; shown when a massager is selected.
   * - Consent/insurance warnings live in `#patient-info` only; consent-empty does NOT hide `#schedule`
   *   (the old `$('#schedule').hide()` for empty consent is commented out in A2).
   * `sync-api`: use GET schedule defaults (rezept main massager) like preselected `<option>`.
   * Explicit `{ massagerId, clinicLocationId }`: user changed/cleared — clear must NOT fall back to API.
   */
  const [massagerSelection, setMassagerSelection] = useState<
    'sync-api' | { massagerId: number | null; clinicLocationId: number | null }
  >('sync-api');
  const [otherCtl, setOtherCtl] = useState(false);
  const [hasDirtyPending, setHasDirtyPending] = useState(false);
  const pendingRef = useRef<Record<string, PendingVisitData>>({});
  const [pendingState, setPendingState] = useState<Record<string, PendingVisitData>>({});
  // Incrementing this remounts VisitClinicCalendarTable, clearing its local pending state
  const [calendarKey, setCalendarKey] = useState(0);

  // Track per-rezept edits: { [rezeptId]: { rezept_yearmonth?, summary?, consent_difference_reason? } }
  const [rezeptChanges, setRezeptChanges] = useState<
    Record<number, Partial<Record<VisitClinicRezeptField, string>>>
  >({});

  // Form change guard: track dirty state to show confirmation dialog on navigation
  const formData = { hasDirtyPending, rezeptChanges };
  const initialFormData = { hasDirtyPending: false, rezeptChanges: {} };
  const { clearFormChange } = useFormChangeGuard(formData, initialFormData);
  const modal = useAppModal();

  const { data, isLoading } = useVisitClinicScheduleQuery(patientId, yearmonth ?? null, serviceType);
  const shouldShowNotFound = !isLoading && !data;

  useEffect(() => {
    if (!patientId) {
      return;
    }

    setLatestEditingPatientId(tenant, patientId, clinic?.clinic_number);
  }, [patientId, tenant, clinic?.clinic_number]);

  useEffect(() => {
    if (!shouldShowNotFound) {
      return;
    }

    clearLatestEditingPatientId(tenant, clinic?.clinic_number);
  }, [shouldShowNotFound, tenant, clinic?.clinic_number]);

  // Only reset from API when changing patients. When changing months: keep the current selection if the massager is still in the new month list.
  useEffect(() => {
    setMassagerSelection('sync-api');
  }, [patientId, serviceType]);

  useEffect(() => {
    pendingRef.current = {};
    setPendingState({});
    setRezeptChanges({});
    setHasDirtyPending(false);
    setOtherCtl(false);
    setCalendarKey(prev => prev + 1);
    clearFormChange();
  }, [serviceType, patientId, yearmonth, clearFormChange]);

  useEffect(() => {
    if (!data?.massagers?.length) return;
    setMassagerSelection(prev => {
      if (prev === 'sync-api') return prev;
      const stillInList = data.massagers.some(m => m.massager_id === prev.massagerId);
      return stillInList ? prev : 'sync-api';
    });
  }, [yearmonth, data]);

  const effectiveMainMassagerId =
    massagerSelection === 'sync-api'
      ? (data?.main_massager_id ?? null)
      : massagerSelection.massagerId;
  const effectiveMainClinicLocationId =
    massagerSelection === 'sync-api'
      ? (data?.main_clinic_location_id ?? null)
      : massagerSelection.clinicLocationId;
  const hasLockedVisit = Object.values(data?.visits ?? {}).some(v => v?.is_locked);
  const hasChangedMainMassager =
    massagerSelection !== 'sync-api' &&
    (massagerSelection.massagerId !== (data?.main_massager_id ?? null) ||
      massagerSelection.clinicLocationId !== (data?.main_clinic_location_id ?? null));

  const storeMutation = useVisitClinicStoreMutation(patientId!, yearmonth!, serviceType);
  const deleteMutation = useVisitClinicBulkDeleteMutation(patientId!, yearmonth!, serviceType);

  const handlePendingChange = useCallback((pending: Record<string, PendingVisitData>) => {
    pendingRef.current = pending;
    setPendingState(pending);
    setHasDirtyPending(Object.values(pending).some(pd => pd.is_dirty));
  }, []);

  const handleClearPendingData = useCallback(() => {
    pendingRef.current = {};
    setPendingState({});
    setHasDirtyPending(false);
  }, []);

  const handleRezeptChange = useCallback(
    (rezeptId: number, field: VisitClinicRezeptField, value: string) => {
      setRezeptChanges(prev => ({
        ...prev,
        [rezeptId]: { ...prev[rezeptId], [field]: value },
      }));
    },
    []
  );

  // Merge rezeptChanges with current rezept data for display
  const rezeptsWithChanges = useMemo(
    () => (data?.rezepts ?? []).map(r => ({ ...r, ...(rezeptChanges[r.id] ?? {}) })),
    [data?.rezepts, rezeptChanges]
  );

  // HIT-15106 (BD 1.3.2 S25, 2026/11/05) — R2 tables whose 「当月施術内容が同意書と異なる理由」 is
  // still missing: red border on their box, from the saved days and what the box shows now.
  const isR2 = serviceType.toUpperCase() === SERVICE_TYPE_R2;
  const reasonRequiredIds = useMemo(
    () =>
      isR2 && data && yearmonth
        ? reasonRequiredRezeptIds(yearmonth, rezeptsWithChanges, data.visits, data.consents)
        : [],
    [isR2, data, yearmonth, rezeptsWithChanges]
  );
  // Set after a 「登録」 that changed マッサージ / 変形徒手矯正 parts, once the month is reloaded:
  // scroll to and focus the first table that still needs its reason.
  const [revealAfterStore, setRevealAfterStore] = useState(0);
  useEffect(() => {
    if (!revealAfterStore) return;
    setRevealAfterStore(0);
    const first = rezeptsWithChanges.find(r => r.id === reasonRequiredIds[0]);
    if (first) revealConsentDifferenceReason(first);
  }, [revealAfterStore, rezeptsWithChanges, reasonRequiredIds]);

  const handleStore = () => {
    if (hasLockedVisit && hasChangedMainMassager) {
      modal.error({
        title: LOCKED_CHANGE_MESSAGE,
        okText: 'OK',
      });
      return;
    }

    const pending = pendingRef.current;

    const updateDates: string[] = [];
    const resetDates: string[] = [];
    const visitsData: VisitClinicStorePayload['visits_data'] = [];

    Object.entries(pending).forEach(([date, pd]) => {
      if (!pd.is_dirty) return;
      if (pd.is_deleted) {
        resetDates.push(date);
      } else {
        updateDates.push(date);
        visitsData.push({
          date,
          visit_id: pd.visit_id,
          massager_id: pd.massager_id,
          clinic_location_id: pd.clinic_location_id,
          patient_address_id: pd.patient_address_id,
          outcome_type_id: pd.outcome_type_id,
          is_initial_inspection_fee: pd.is_initial_inspection_fee,
          is_acupuncture: pd.is_acupuncture,
          is_moxibustion: pd.is_moxibustion,
          electric_treatment: pd.electric_treatment,
          is_not_hot_treatment: pd.is_not_hot_treatment,
          massage: pd.massage,
          orthomorphia: pd.orthomorphia,
          is_insurance: pd.is_insurance,
          massager_remarks: pd.massager_remarks,
          // 令和8年法改正 No 5 — only a hand-tick travels. Left out, the server keeps
          // deriving the 明細書発行加算 flag from the patient's 明細書発行設定.
          ...(pd.is_meisai_issue_user_changed
            ? {
                is_meisai_issue: !!pd.is_meisai_issue,
                is_meisai_issue_user_changed: true,
              }
            : {}),
          previous_visit_id: pd.previous_visit_id,
        });
      }
    });

    // Build rezept_data from tracked changes.
    // For enabled rezepts with null rezept_yearmonth (not yet set), default to next month
    // of treatement_yearmonth (BD §8.4: 提出年月 defaults to the month after 施術年月).
    const rezeptData: VisitClinicStorePayload['rezept_data'] = (data?.rezepts ?? [])
      .map(r => {
        const changes = rezeptChanges[r.id] ?? {};
        const isEditable = r.all_status === 0 && !r.is_return;

        let ym = changes.rezept_yearmonth;
        if (ym === undefined && isEditable && !r.rezept_yearmonth) {
          // treatement_yearmonth is an integer in DB; use String() to handle number safely
          const tm = String(r.treatement_yearmonth ?? '');
          if (tm.length >= 6) {
            ym = dayjs(`${tm.slice(0, 4)}-${tm.slice(4, 6)}-01`)
              .add(1, 'month')
              .format('YYYYMM');
          }
        }

        if (
          ym !== undefined ||
          changes.summary !== undefined ||
          changes.consent_difference_reason !== undefined
        ) {
          return {
            rezept_id: r.id,
            ...(ym !== undefined && { rezept_yearmonth: ym }),
            ...(changes.summary !== undefined && { summary: changes.summary }),
            ...(changes.consent_difference_reason !== undefined && {
              consent_difference_reason: changes.consent_difference_reason,
            }),
          };
        }
        return null;
      })
      .filter((rd): rd is NonNullable<typeof rd> => rd !== null);

    const payload: VisitClinicStorePayload = {
      main_massager_id: effectiveMainMassagerId,
      // Pair with main_massager_id (BE invariant: required_with). Picker tracks both.
      main_clinic_location_id:
        effectiveMainMassagerId !== null ? effectiveMainClinicLocationId : null,
      update_dates: updateDates,
      reset_dates: resetDates,
      otherCtl,
      rezept_data: rezeptData,
      visits_data: visitsData,
    };

    const changedParts =
      isR2 && !!data && storeChangesR2Parts(pending, data.visits, data.consents);

    storeMutation.mutate(payload, {
      onSuccess: () => {
        pendingRef.current = {};
        setPendingState({});
        setRezeptChanges({});
        setHasDirtyPending(false);
        clearFormChange();
        setCalendarKey(prev => prev + 1);
        void resetVisitClinicScheduleForPatient(queryClient, patientId!).then(() => {
          if (changedParts) setRevealAfterStore(prev => prev + 1);
        });
      },
    });
  };

  const handleBulkDelete = () => {
    // BD §5.3: pre-check lock / 訪問入力 before confirming. If both apply, show
    // both dialogs one after the other (lock first, 訪問入力 after it is closed);
    // delete is aborted either way.
    const visits = data?.visits ?? {};
    const hasLocked = Object.values(visits).some(v => v?.is_locked);
    const hasHouseCall = Object.values(visits).some(
      v => v?.previous_visit_id != null && v.previous_visit_id !== -1
    );

    const runDelete = () => {
      deleteMutation.mutate(undefined, {
        onSuccess: () => {
          pendingRef.current = {};
          setPendingState({});
          setRezeptChanges({});
          setHasDirtyPending(false);
          clearFormChange();
          setCalendarKey(prev => prev + 1);
          void resetVisitClinicScheduleForPatient(queryClient, patientId!);
        },
      });
    };

    const openConfirm = () => {
      modal.confirm({
        title: '施術（来院）を削除します。よろしいですか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: runDelete,
      });
    };

    const openHouseCallDialog = () => {
      modal.error({
        title: '訪問入力を削除する場合は訪問画面から削除してください。',
        okText: 'OK',
      });
    };

    if (hasLocked) {
      modal.error({
        title: 'ロックされているため、編集及び削除はできません。',
        okText: 'OK',
        onOk: hasHouseCall ? openHouseCallDialog : undefined,
      });
      return;
    }
    if (hasHouseCall) {
      openHouseCallDialog();
      return;
    }

    openConfirm();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <AppSpin />
      </div>
    );
  }

  if (!data) {
    return <NotFound />;
  }

  const hasNoInsurance = !data.has_health_insurance;
  const hasNoConsent = data.consents.length === 0;
  // A2 patient-info: two independent @if — both red lines can show.
  const showInsuranceWarning = hasNoInsurance;
  const showConsentWarning = hasNoConsent;

  // `#schedule` / panels: A2 `select_massager_id` non-empty → show. API mirrors A2 `mainMassager`:
  // rezept main_massager_id, else if visits exist → first massager in list (VisitClinicController editInit).
  // Guard: if `main_massager_id` is stale (not in `massagers`), avoid opening panels with broken select.
  const mainMassagerInList =
    effectiveMainMassagerId != null &&
    data.massagers.some(m => m.massager_id === effectiveMainMassagerId);
  const showSchedulePanels = mainMassagerInList;

  return (
    <div className={`${showSchedulePanels ? 'pb-4' : ''}`}>
      {/* ── Search bar + 施術年月 ────────────────────────── */}
      <div className="mb-3">
        <VisitClinicSearchBar
          yearmonth={yearmonth}
          onYearmonthChange={ym => navigate(`/${tenant}/visit_clinic/edit/${patientId}/${ym}`)}
        />
      </div>

      {/* ── Massager select + advisory ─────────────────── */}
      <div className="flex flex-wrap items-center w-full gap-2 mb-3 text-sm">
        <span>施術師</span>
        <VisitClinicMassagerSelect
          massagers={data.massagers}
          value={effectiveMainMassagerId}
          clinicLocationId={effectiveMainClinicLocationId}
          placeholder="選択してください"
          onChange={(mid, locId) => {
            setMassagerSelection({ massagerId: mid, clinicLocationId: locId });
          }}
          onClearPendingData={handleClearPendingData}
          hasUnsavedData={hasDirtyPending}
          isChangeBlocked={hasLockedVisit}
          blockedMessage={LOCKED_CHANGE_MESSAGE}
          disabled={storeMutation.isPending || deleteMutation.isPending}
        />
        <span className="text-xs text-gray-600">
          転帰（治癒・中止・転医）入力は、施術カレンダーの受診日を選択→【詳細】ボタンから行ってください。
        </span>
      </div>

      {/* ── Patient header ─────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-end gap-4">
          <span className="text-base">患者氏名</span>
          <span className="text-2xl font-bold">{data.patient.name}</span>
          <span className="text-base ">受診者番号：[{data.patient.serial_number}]</span>
        </div>
        <span className="text-base">[請求区分][{data.patient.division ?? ''}]</span>
      </div>

      {/* ── Warning banners (Section 6) ────────────────── */}
      {showInsuranceWarning && (
        <p className="mb-1 text-2xl font-bold text-red-600">※保険証が存在していません</p>
      )}
      {showConsentWarning && (
        <p className="mb-1 text-2xl font-bold text-red-600">※同意書が存在していません</p>
      )}

      {showSchedulePanels && (
        <>
          {/* ── Panel 1: 施術カレンダー ────────────────────── */}
          <CollapsibleCard
            className="mt-4"
            title="施術カレンダー"
            extraHeaderContent={
              <div className="flex gap-2">
                <AppButton
                  size="small"
                  disabled={storeMutation.isPending}
                  onClick={() => navigate(`/${tenant}/patients/edit/${patientId}`)}
                >
                  保険証
                </AppButton>
                <AppButton
                  size="small"
                  disabled={storeMutation.isPending}
                  onClick={() => navigate(`/${tenant}/consents/${patientId}/list`)}
                >
                  同意書
                </AppButton>
              </div>
            }
          >
            <VisitClinicCalendarTable
              patientId={patientId}
              storeLoading={storeMutation.isPending}
              key={calendarKey}
              yearmonth={yearmonth!}
              serviceType={serviceType}
              visits={data.visits}
              massagers={data.massagers}
              mainMassagerId={effectiveMainMassagerId}
              mainClinicLocationId={effectiveMainClinicLocationId}
              outcomeTypes={data.outcome_types}
              consents={data.consents}
              rezepts={rezeptsWithChanges}
              initialInspectionPrices={data.initial_inspection_prices}
              onChange={handlePendingChange}
            />
          </CollapsibleCard>
          {/* ── Panel 2: 料金｜コメント — shows whenever patient is official (BD §8) ── */}
          <CollapsibleCard className="mt-4" title="料金｜コメント">
            <VisitClinicPriceSection
              serviceType={serviceType}
              rezepts={rezeptsWithChanges}
              visits={data.visits}
              pending={pendingState}
              initialInspectionPrices={data.initial_inspection_prices}
              onRezeptChange={handleRezeptChange}
              reasonRequiredRezeptIds={reasonRequiredIds}
            />
          </CollapsibleCard>
          {/* ── Panel 3: 同意書情報 ────────────────────────── */}
          <CollapsibleCard title="同意書情報">
            <VisitClinicConsentSection consents={data.consents} />
          </CollapsibleCard>
          {/* ── Panel 4: 保険証情報 ────────────────────────── */}
          <CollapsibleCard title="保険証情報">
            <VisitClinicInsuranceSection summary={data.health_insurance.summary} />
          </CollapsibleCard>
          <BottomVisitClinicBar
            deleteLoading={deleteMutation.isPending}
            storeLoading={storeMutation.isPending}
            otherCtlChecked={otherCtl}
            onOtherCtlChange={setOtherCtl}
            onBulkDelete={handleBulkDelete}
            onStore={handleStore}
          />
        </>
      )}
    </div>
  );
};

export default VisitClinicEditPage;
